/**
 * Popup Manager — shared coordinator for sitewide popups
 * ------------------------------------------------------
 * Loaded from <head> in layout/theme.liquid after constants.js/pubsub.js and
 * before the popup scripts (which are deferred near </body>), so it is defined
 * before they run and `subscribe`/`PUB_SUB_EVENTS` are defined before it runs.
 *
 * Owns four things that used to be duplicated — and fought — across the
 * individual popup scripts:
 *
 *   1. Mutual exclusion. Only one popup may be open at a time. A popup that
 *      asks to open while another is open is refused, so two dialogs can never
 *      stack on top of each other.
 *   2. Body scroll lock. Previously each script wrote document.body.style
 *      .overflow directly; when two popups overlapped, whichever closed last
 *      either unlocked the page under an open dialog or left it locked
 *      forever. The lock is now owned here and tied to the single open popup.
 *   3. The background blur. Adding `popup-open` to <body> blurs every direct
 *      child of <body> except those marked `.popup-layer` — see
 *      assets/popup-manager.css.
 *   4. The cart-count cache and the mobile back-button trap — see the two
 *      sections below for why both have to be shared rather than per-popup.
 *
 * Usage from a popup script:
 *
 *   if (!window.ThemePopups.canOpen('exit-intent')) return;   // someone else is up
 *   window.ThemePopups.open('exit-intent');                   // ...show your markup
 *   window.ThemePopups.close('exit-intent');                  // ...hide your markup
 *
 * A popup that must never be skipped passes `{ force: true }` to open(), which
 * evicts whatever is on screen instead of being refused. Only the checkout gate
 * does this. Every popup that can be evicted has to listen for the
 * `theme-popups:preempt` event and hide itself when named.
 *
 * The manager never touches popup markup itself — callers still own showing
 * and hiding their own dialog.
 */

(function () {
  'use strict';

  /*
   * Body classes the rest of the theme uses for its own overlays (cart drawer,
   * cart notification, quick add, header search, menu drawer, pickup
   * availability). They are not registered with this manager, so we detect
   * them by class to avoid opening a popup on top of one of them.
   */
  var FOREIGN_OVERLAY_CLASSES = [
    'overflow-hidden',
    'overflow-hidden-mobile',
    'overflow-hidden-tablet',
    'overflow-hidden-desktop',
  ];

  var BODY_OPEN_CLASS = 'popup-open';

  /*
   * Fired at the popup that is being evicted by a forced open(). The manager
   * never touches popup markup, so eviction is an announcement: the owner hides
   * its own dialog in response. See open() for what happens if it doesn't.
   */
  var PREEMPT_EVENT = 'theme-popups:preempt';

  var openId = null;
  var previousBodyOverflow = '';

  /*
   * How long after a popup closes the mobile back-button trap stays disarmed.
   *
   * The exit-intent popup re-fires on every exit attempt rather than once a day,
   * so the trap can no longer be a one-shot. Re-pushing a history entry on every
   * back press would mean the visitor could never leave the page at all — a trap
   * in the literal sense, not a nag. Keeping a window right after each dismissal
   * in which no entry is armed guarantees back always has a way out.
   *
   * Lives here, not in the popup scripts, because both popups call armBackTrap()
   * and the window has to be shared: whichever one re-arms first would otherwise
   * cancel the other's grace period.
   */
  var BACK_TRAP_COOLDOWN_MS = 3000;

  var lastCloseAt = 0;
  var backTrapTimer = null;

  /*
   * Whether any popup uses the back-button trap at all. Set the first time
   * armBackTrap() is called, which only happens on touch devices. close() reads
   * it to decide whether to schedule a re-arm, so a desktop popup closing never
   * conjures a history entry for a mechanism nothing is using.
   */
  var backTrapInUse = false;

  function foreignOverlayOpen() {
    var body = document.body;
    if (!body) return false;

    for (var i = 0; i < FOREIGN_OVERLAY_CLASSES.length; i++) {
      if (body.classList.contains(FOREIGN_OVERLAY_CLASSES[i])) return true;
    }

    return false;
  }

  // =========================================
  // SHARED CART-COUNT CACHE
  //
  // The popups gate on cart contents — one fires only when the cart has items,
  // the other only when it's empty. Each script used to keep its own count,
  // fetched once at page load, with no invalidation. Two failures came out of
  // that:
  //
  //   - An AJAX add-to-cart never reached the cache, so the empty-cart popup
  //     would open off a count of 0 that was minutes stale, then retract itself
  //     when the background re-check disagreed. Visitors saw it flash.
  //   - Whichever popup didn't apply at page load never armed its triggers at
  //     all, so after the cart changed the popup that *should* have fired had
  //     no listeners attached.
  //
  // The count now lives here, is invalidated the moment a cart mutation request
  // goes out (see PENDING CART MUTATIONS below) as well as on `cartUpdate`, and
  // carries a timestamp so callers can distinguish "cart has items" from "cart
  // had items 4 minutes ago". Concurrent refreshes share one request.
  //
  // Freshness is a hint, never a substitute for verification: apps that mutate
  // the cart without publishing cartUpdate exist, which is what the TTL is for
  // — a stale cache just costs callers a /cart.js round trip.
  // =========================================
  // =========================================
  // FOCUS TRAP
  //
  // A dialog that declares aria-modal="true" has to keep Tab inside itself. The
  // exit-intent popups declared it without trapping anything, so keyboard users
  // could Tab straight out into the page behind — which is also how a checkout
  // button underneath an open popup could still be activated, since the blur's
  // `pointer-events: none` stops the mouse but not the keyboard.
  //
  // The theme's own global trapFocus() isn't usable here: it snapshots
  // querySelectorAll results without filtering hidden nodes, and these popups
  // keep a display:none form-sink <iframe> and a `hidden` success view inside the
  // dialog. It would pick an unfocusable node as "last" and Tab would leak out.
  //
  // This version recomputes on every Tab and skips anything not rendered, so a
  // popup that swaps views mid-flight needs no re-arming.
  // =========================================
  var FOCUSABLE_SELECTOR = [
    'a[href]',
    'area[href]',
    'button:not([disabled])',
    'input:not([type="hidden"]):not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'summary',
    'iframe',
    'object',
    '[tabindex]:not([tabindex^="-"])',
  ].join(', ');

  var trapContainer = null;

  function visibleFocusables(container) {
    return Array.prototype.filter.call(container.querySelectorAll(FOCUSABLE_SELECTOR), function (el) {
      /* Empty rect list = display:none, or inside a [hidden] ancestor. */
      return el.getClientRects().length > 0;
    });
  }

  function onTrapKeydown(event) {
    if (event.key !== 'Tab' || !trapContainer) return;

    var elements = visibleFocusables(trapContainer);
    if (!elements.length) return;

    var first = elements[0];
    var last = elements[elements.length - 1];

    /* Focus escaped the dialog (stray programmatic focus, browser chrome) —
       pull it back instead of letting Tab walk the page behind. */
    if (!trapContainer.contains(document.activeElement)) {
      event.preventDefault();
      first.focus();
      return;
    }

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  var CART_TTL_MS = 15000;
  var CART_EVENT = 'theme-popups:cart';

  var cartCount = null;   // last known item_count, or null while unknown
  var cartFetchedAt = 0;  // when cartCount was recorded; 0 means "stale"
  var cartInFlight = null;

  /*
   * Bumped every time the cart is known to have moved — a mutation request going
   * out, or a cartUpdate landing. A /cart.js answer is cached only if the
   * generation it was issued under is still current; otherwise the cart changed
   * under it and the answer describes a state that no longer exists.
   */
  var cartGeneration = 0;
  var cartInFlightGeneration = -1;

  // =========================================
  // PENDING CART MUTATIONS
  //
  // Time-based freshness on its own was not enough, and this is the bug that came
  // out of it: a visitor lands with an empty cart (count 0, cached and fresh for
  // CART_TTL_MS), adds an item, and flicks the cursor to the top of the window
  // while the add request is still on the wire. The empty-cart video popup read a
  // count that was seconds old — fresh by the clock, already wrong in fact —
  // opened on it, then retracted itself when its own re-check came back with 1.
  // A popup flashed on screen for a moment, and it was the wrong popup for that
  // cart.
  //
  // cartUpdate cannot close that window: it is published when the add RESPONSE
  // lands, so the whole gap between the click and the response is unguarded.
  //
  // So the cache is invalidated when the request GOES OUT, by wrapping fetch and
  // watching for cart mutation URLs. refreshCart() also waits for in-flight
  // mutations to settle before asking /cart.js — a read issued mid-add can come
  // back with the pre-add count, which is worse than not reading at all, because
  // it would be stamped fresh.
  //
  // Only fetch is wrapped. A third-party app mutating the cart over
  // XMLHttpRequest is invisible here; the TTL and the popups' own re-check stay
  // as the backstop for that.
  // =========================================
  var CART_MUTATION_URL = /\/cart\/(add|change|update|clear)(\.js|\.json)?($|[?#])/i;

  /* A mutation whose promise never settles must not wedge every popup for the
     rest of the page. */
  var MUTATION_TIMEOUT_MS = 10000;

  var pendingMutations = 0;
  var settleWaiters = [];

  function invalidateCart() {
    cartGeneration++;
    cartFetchedAt = 0;
  }

  function onMutationStart() {
    pendingMutations++;
    invalidateCart();
  }

  function onMutationSettle() {
    if (pendingMutations > 0) pendingMutations--;
    if (pendingMutations > 0) return;

    var waiters = settleWaiters;
    settleWaiters = [];
    for (var i = 0; i < waiters.length; i++) waiters[i]();
  }

  function whenMutationsSettle() {
    if (pendingMutations === 0) return Promise.resolve();

    return new Promise(function (resolve) {
      settleWaiters.push(resolve);
    });
  }

  if (typeof window.fetch === 'function') {
    var nativeFetch = window.fetch;

    window.fetch = function (input) {
      var url = '';
      try {
        url = typeof input === 'string' ? input : (input && input.url) || '';
      } catch (e) { /* exotic input — treat as not a cart mutation */ }

      if (!CART_MUTATION_URL.test(url)) return nativeFetch.apply(this, arguments);

      onMutationStart();

      var settled = false;
      var backstop = setTimeout(finish, MUTATION_TIMEOUT_MS);

      function finish() {
        if (settled) return;
        settled = true;
        clearTimeout(backstop);
        onMutationSettle();
      }

      var result;
      try {
        result = nativeFetch.apply(this, arguments);
      } catch (e) {
        finish();
        throw e;
      }

      if (!result || typeof result.then !== 'function') {
        finish();
        return result;
      }

      /*
       * This handler is attached before the caller chains its own, so the
       * mutation is marked settled before the caller parses the response and
       * publishes cartUpdate. onCartUpdate therefore never runs while its own
       * request still counts as pending.
       */
      return result.then(
        function (response) {
          finish();
          return response;
        },
        function (error) {
          finish();
          throw error;
        }
      );
    };
  }

  function fetchCart() {
    /* no-store: a /cart.js served out of the HTTP cache hands back the pre-add
       count and looks perfectly fresh doing it. */
    return fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' })
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .then(function (data) {
        return data && typeof data.item_count === 'number' ? data.item_count : null;
      })
      .catch(function () {
        return null;
      });
  }

  /*
   * How many times a single refreshCart() may re-read before giving up and
   * answering with what it has. Each repeat costs a round trip, so this is a
   * bound on a cart being hammered (quantity spinner held down), not a retry
   * policy for network errors — a failed read resolves with null immediately.
   */
  var MAX_READ_ATTEMPTS = 3;

  /*
   * One /cart.js read, taken only once every in-flight mutation has settled.
   *
   * If the cart moves again while the read is on the wire, its answer describes a
   * cart that no longer exists: it is neither cached nor handed back, and the
   * read is repeated. Returning it would defeat the point of the wait — a caller
   * about to decide whether to open a popup would be deciding on a count it
   * already knows to be superseded.
   */
  function readCart(attempt) {
    var generation = -1;

    var request = whenMutationsSettle()
      .then(function () {
        /* Read the generation here rather than at call time: mutations may have
           started and settled while this request waited its turn. */
        generation = cartGeneration;
        cartInFlightGeneration = generation;
        return fetchCart();
      })
      .then(function (count) {
        if (cartInFlight === request) cartInFlight = null;

        if (count !== null && cartGeneration === generation) {
          cartCount = count;
          cartFetchedAt = Date.now();
          document.dispatchEvent(new CustomEvent(CART_EVENT, { detail: { count: count } }));
          return count;
        }

        if (count !== null && attempt < MAX_READ_ATTEMPTS) return startRead(attempt + 1);

        /* Read failed (null), or out of attempts. Either way the cache is left
           alone and the caller gets "unknown" or a count it must verify itself. */
        return count;
      });

    cartInFlight = request;
    cartInFlightGeneration = cartGeneration;

    return request;
  }

  /*
   * Share an in-flight read only while it still answers the current generation.
   * One that went out before a mutation is describing the old cart, so a caller
   * asking now has to start its own.
   */
  function startRead(attempt) {
    if (cartInFlight && cartInFlightGeneration === cartGeneration) return cartInFlight;
    return readCart(attempt);
  }

  var ThemePopups = {
    /* Which popup currently holds the screen, or null. */
    current: function () {
      return openId;
    },

    isAnyOpen: function () {
      return openId !== null;
    },

    /*
     * True when `id` is allowed to open right now. Re-asking for the popup
     * that is already open returns false — callers should treat that as "no
     * work to do", which matches their existing "already visible" guards.
     *
     * `{ force: true }` is for a popup that outranks whatever is on screen —
     * currently only the checkout gate. It may evict another popup and may open
     * over a theme drawer, neither of which is a reason to skip a dialog that
     * gates checkout.
     */
    canOpen: function (id, options) {
      var force = !!(options && options.force);

      if (openId !== null) return openId !== id && force;
      return force || !foreignOverlayOpen();
    },

    /*
     * Claim the screen for `id`: lock scrolling and blur the page behind.
     * Returns false if the claim was refused, in which case the caller must
     * not reveal its markup.
     */
    open: function (id, options) {
      if (!this.canOpen(id, options)) return false;

      if (openId !== null) {
        /*
         * Ask the incumbent to hide itself, then take the lock either way.
         * Taking it unconditionally is deliberate: if the incumbent ignores the
         * event, ownership must still move here, because otherwise its own
         * eventual close() would match and unlock the page — and un-blur it,
         * making the page behind clickable — underneath THIS dialog.
         */
        document.dispatchEvent(new CustomEvent(PREEMPT_EVENT, { detail: { id: openId } }));
      }

      /*
       * Only record the pre-popup overflow when nothing holds the lock. On a
       * clean eviction the incumbent's close() has already restored it, so this
       * reads the real page value; on a botched one the saved value is still the
       * original and must not be overwritten with the popup's own 'hidden'.
       */
      if (openId === null) previousBodyOverflow = document.body.style.overflow;

      openId = id;

      document.body.style.overflow = 'hidden';
      document.body.classList.add(BODY_OPEN_CLASS);

      return true;
    },

    /*
     * Release the screen. Ignored unless `id` is the popup that currently
     * holds it, so a stray close from a popup that was never opened cannot
     * unlock the page under a different dialog.
     */
    close: function (id) {
      if (openId !== id) return;

      openId = null;
      lastCloseAt = Date.now();

      document.body.style.overflow = previousBodyOverflow;
      previousBodyOverflow = '';
      document.body.classList.remove(BODY_OPEN_CLASS);

      /*
       * Bring the back trap back once the escape window expires. This has to be
       * driven from here rather than from the popup scripts: armBackTrap() skips
       * while a dialog is open and during the cooldown, so after a dismissal
       * nothing would ever call it again and the trigger would go quiet for the
       * rest of the page.
       */
      if (backTrapInUse) {
        clearTimeout(backTrapTimer);
        backTrapTimer = setTimeout(function () {
          ThemePopups.armBackTrap();
        }, BACK_TRAP_COOLDOWN_MS);
      }
    },

    /*
     * Keep Tab inside `container` and move focus to `elementToFocus` (or the
     * first rendered focusable). Safe to call again to re-point focus.
     */
    trapFocus: function (container, elementToFocus) {
      if (!container) return;

      trapContainer = container;
      document.addEventListener('keydown', onTrapKeydown, true);

      var target = elementToFocus || visibleFocusables(container)[0];
      if (target && typeof target.focus === 'function') target.focus();
    },

    /* Release the trap and hand focus back to whatever opened the dialog. */
    releaseFocus: function (restoreTo) {
      trapContainer = null;
      document.removeEventListener('keydown', onTrapKeydown, true);

      if (restoreTo && typeof restoreTo.focus === 'function') restoreTo.focus();
    },

    /* Last known cart item_count, or null if it has never been read. */
    cartCount: function () {
      return cartCount;
    },

    /*
     * True when cartCount() is recent enough to act on without re-checking.
     * False after a cartUpdate until the refresh lands, and after the TTL.
     */
    isCartFresh: function () {
      if (cartCount === null || cartFetchedAt === 0) return false;
      return Date.now() - cartFetchedAt < CART_TTL_MS;
    },

    /*
     * Re-read /cart.js and resolve with the count. Resolves with null when the
     * request fails — that means "unknown", NOT "empty", and the cache keeps
     * its previous value. Callers must not treat null as a zero count.
     * Concurrent callers share one in-flight request.
     *
     * Waits out any in-flight cart mutation first, and repeats the read if the
     * cart moves again mid-flight — see readCart().
     */
    refreshCart: function () {
      return startRead(1);
    },

    /*
     * Mobile back-button trap, shared so that N armed popups push ONE history
     * entry between them instead of one each. Pushing per-popup meant every
     * back press was swallowed by a different popup's entry and the visitor
     * could not leave the page.
     *
     * Three guards, and each closes a different hole:
     *
     *   1. An entry is already armed → skip. Stops popups stacking traps on each
     *      other, and stops a re-arm from doubling up on a live one.
     *   2. A popup is currently on screen → skip. Re-arming while the dialog is
     *      up leaves a live entry behind after it closes, and that entry then
     *      silently swallows the visitor's first back press — they press back,
     *      nothing happens, and they have to press again. Worse than useless,
     *      because it eats the escape window guard 3 is there to create.
     *   3. A popup closed less than BACK_TRAP_COOLDOWN_MS ago → defer the arm to
     *      the end of that window instead of dropping it. Back stays usable right
     *      after a dismissal, which is the visitor's guaranteed way out, and the
     *      trap still comes back so the next exit attempt is caught.
     *
     * Deferring rather than dropping is the important half of guard 3: dropping
     * would leave the trap disarmed for good once a popup had been closed.
     */
    armBackTrap: function () {
      backTrapInUse = true;

      if (history.state && history.state.themePopupTrap) return;
      if (openId !== null) return;

      var wait = BACK_TRAP_COOLDOWN_MS - (Date.now() - lastCloseAt);
      if (wait > 0) {
        clearTimeout(backTrapTimer);
        backTrapTimer = setTimeout(function () {
          ThemePopups.armBackTrap();
        }, wait);
        return;
      }

      history.pushState({ themePopupTrap: true }, '');
    },
  };

  /*
   * Invalidate immediately, then refresh. Marking stale before the request
   * lands matters: a trigger firing in that window must take the verify-first
   * path rather than acting on the pre-update count. Bumping the generation is
   * what stops a /cart.js read that is already on the wire — issued before this
   * change — from landing afterwards and being stamped fresh.
   *
   * The cart/change and cart/update responses carry the whole cart, so the new
   * count is already in the event and needs no round trip. A cart/add response
   * carries the added line item instead, which has no item_count on it, so that
   * path still has to ask.
   */
  function onCartUpdate(event) {
    invalidateCart();

    var data = event && event.cartData;
    if (data && typeof data.item_count === 'number') {
      cartCount = data.item_count;
      cartFetchedAt = Date.now();
      document.dispatchEvent(new CustomEvent(CART_EVENT, { detail: { count: data.item_count } }));
      return;
    }

    ThemePopups.refreshCart();
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, onCartUpdate);
  }

  window.ThemePopups = ThemePopups;

  /* Warm the cache once for every popup, instead of once per popup script. */
  ThemePopups.refreshCart();
})();
